import { CategoryPreferences } from '@segment/consent-manager/types/types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type CookiesConsentManagerContextType = {
  ready: boolean;
  cookiesSettings: CategoryPreferences;
  updateCookiesSettings(settings: CategoryPreferences): void;
};

const defaultCookiesConsentManagerContext: CookiesConsentManagerContextType = {
  ready: false,
  cookiesSettings: {
    marketingAndAnalytics: false,
    advertising: false,
  },
  updateCookiesSettings: () => undefined,
};

export const CookiesConsentManagerContext = createContext<CookiesConsentManagerContextType>(defaultCookiesConsentManagerContext);
const ANALYTICS_LOADING_TIMEOUT = 1000;
const ANALYTICS_POLLING_INTERVAL = 200;

export const CookiesConsentManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState<boolean>(false);
  const [cookiesSettings, setCookiesSettings] = useState<CategoryPreferences>({});
  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const intervalRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      intervalRef.current = setInterval(() => {
        if (window.analytics) {
          setReady(true);
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      }, ANALYTICS_POLLING_INTERVAL);

      timeoutRef.current = setTimeout(() => {
        if (!window.analytics) {
          setReady(true);
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      }, ANALYTICS_LOADING_TIMEOUT);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const updateCookiesSettings = useCallback(
    (settings: CategoryPreferences) => {
      if (Object.keys(settings).length > 0) {
        setCookiesSettings({
          ...cookiesSettings,
          ...settings,
        });
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setReady(true);
    },
    [cookiesSettings],
  );

  const contextValue = useMemo(
    () => ({
      ready,
      cookiesSettings,
      updateCookiesSettings,
    }),
    [ready, cookiesSettings, updateCookiesSettings],
  );

  return <CookiesConsentManagerContext.Provider value={contextValue}>{children}</CookiesConsentManagerContext.Provider>;
};

export const useCookiesConsentManagerContext = (): CookiesConsentManagerContextType => useContext(CookiesConsentManagerContext);
